/**
 * @description TanStack Query mutation for creating/updating pre-orders
 * @author Omar Sanchez Martinez
 * @since 2025-10-17
 */

import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { createPreOrderService } from '../services/payments.service';
import type { PaymentGatewayApiResponse } from '../types/payment.interface';
import { buyCreditsKeys } from './queryKeys';

interface CreatePreOrderVariables {
  subscriptionPlanId: number;
  preOrderId?: string;
}

/**
 * @description Hook for creating or updating a pre-order for credit purchase
 * Returns client_secret and pre_order_id needed for Stripe Payment Element
 */
export const useCreatePreOrder = () => {
  const queryClient = useQueryClient();

  return useMutation<
    PaymentGatewayApiResponse,
    AxiosError<{ errors?: Record<string, string[]> }>,
    CreatePreOrderVariables
  >({
    mutationFn: ({ subscriptionPlanId, preOrderId }: CreatePreOrderVariables) => {
      return createPreOrderService(subscriptionPlanId, preOrderId);
    },

    onSuccess: () => {
      // Invalidate pre-orders queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: buyCreditsKeys.preOrders(),
      });
    },

    onError: (error: AxiosError<{ errors?: Record<string, string[]> }>) => {
      console.error('Error creating pre-order:', error);
    },

    // Retry logic for better resilience
    retry: (failureCount: number, error: AxiosError) => {
      // Don't retry on validation errors (4xx status codes)
      if (error?.response?.status && error.response.status >= 400 && error.response.status < 500) {
        return false;
      }

      // Retry up to 2 times for network/server errors
      return failureCount < 2;
    },

    // Retry delay with exponential backoff
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
